/**
 * A small identity asset with a real, designed failure state.
 *
 * Asset failure is not an edge case on this surface. One CDN answers 403 with
 * `cf-mitigated: challenge` and `cross-origin-resource-policy: same-origin`;
 * another answers 401. No referrer policy, no `crossorigin` attribute and no
 * proxy-free trick recovers either, so a quarter of two pillars' rows will
 * always render the fallback. It has to be good enough to ship as the normal
 * state, and it has to be honest:
 *
 *   1. No `src` at all renders the fallback immediately — no request, no error
 *      path, no flash.
 *   2. `fallbackSrc` is a NAMED, VISIBLE plate the caller supplies — the member
 *      placeholder image on a profile surface, ruled 2026-08-22 — and it is
 *      drawn in the MONOGRAM's place, never handed to the primary `Cimage`.
 *      What stays banned is what the ban was always about: a transparent-pixel
 *      fallback, which converts a visible failure into an invisible one. Routing
 *      it through the primary image would also be mutually destructive with the
 *      degenerate-asset guard below, because the guard measures whatever the
 *      primary image decoded. So the two never share an element: the primary
 *      `src` keeps its guard, and the fallback is a second, separate image that
 *      only exists once the primary is Absent or Failed.
 *   3. Because no pixel is ever injected into the primary image, a decoded 1×1
 *      there can only be a tracking-pixel response — so it is treated as a
 *      failure rather than rendered as a blank mark.
 *   4. The failure plate is visually DISTINCT from a supplied monogram: a
 *      dashed hairline rather than the solid well, and a label that says the
 *      logo is unavailable. The initials — or the supplied `fallbackSrc` plate
 *      — stay, because they are still the best available identity; they just
 *      stop claiming to be the mark. An ABSENT asset keeps the solid hairline:
 *      a wire that published no picture did not fail a check.
 *   5. No retry, no host allowlist, no proxy. A bot-challenged 403 is
 *      permanent and a hardcoded dead-host list is a seam.
 *   6. The plate is present from first paint and never animates. A fallback
 *      that fades in advertises a failure; one that is simply there reads as
 *      the design.
 *   7. The derived monogram is a DEFAULT, not the contract. Initials taken from
 *      a name are wrong for a whole class of real identities — `The New York
 *      Times` derives `TN` and `J. R. R. Tolkien` derives `JR` — and counting
 *      more words does not repair either: the answers a reader wants are `NYT`
 *      and `JT`, which are not the first letters of the first N words in
 *      order. §8.16 asks for `eB` and `AZ` for eBay and Amazon, which no
 *      derivation from `name` produces at all. So the derivation stays at the
 *      first two significant words — the shape §4.1 states, and the only one
 *      that fits the 22px Row mark, where three or four glyphs at 8px overrun
 *      the plate — and a caller that knows the right monogram states it.
 */
"use client";
import {
  useCallback,
  useEffect,
  useState,
  type ReactElement,
  type SyntheticEvent,
} from "react";
import {
  Cimage,
  CIMAGE_ANIMATION,
  CIMAGE_OBJECT_FIT,
} from "@/components/ui/Cimage";
import {
  CRC_MEDIA_DECODING,
  CRC_MEDIA_LOADING,
  CRC_MEDIA_REFERRER_POLICY,
} from "./cresultcard.media";
import { useCresultcardNestedRadius } from "./cresultcard.radius";
import {
  CRC_MARK_PIXELS,
  CRC_TEXT_MUTED,
  CRC_TILE_BACKGROUND_COLOR,
} from "./cresultcard.tokens";
import {
  CresultcardMarkAssetState,
  CresultcardMarkShape,
  CresultcardMarkSize,
} from "./cresultcard.types";
import { cresultcardMonogram } from "./CresultcardMediaFallback";

const DEGENERATE_PIXEL_WIDTH = 1;
const SOLID_HAIRLINE = "1px solid rgba(255,255,255,0.09)";
const DASHED_HAIRLINE = "1px dashed rgba(255,255,255,0.18)";
const MONOGRAM_SIZE_RATIO = 0.36;
const CIRCLE_RADIUS = "50%";

interface CresultcardIdentityMarkProps {
  readonly src?: string;
  /** The identity itself. It is the monogram, the alt text and the label. */
  readonly name: string;
  readonly size?: CresultcardMarkSize;
  readonly shape?: CresultcardMarkShape;
  /**
   * The monogram to draw, where the caller knows it and the derivation cannot
   * reach it: `NYT`, `eB`, `AZ`. Absent, the first two significant words of
   * `name` are used.
   */
  readonly monogram?: string;
  /**
   * A named placeholder ASSET that beats initials for this kind of identity —
   * the member placeholder image on a profile or an author, which is the ruled
   * answer for a person-shaped mark whose wire published no picture or whose
   * picture died (2026-08-22). It fills the same plate the monogram would, and
   * it covers BOTH states with one prop: `Absent` (no `src` was given) and
   * `Failed` (the host declined, or answered with a tracking pixel).
   */
  readonly fallbackSrc?: string;
  /** A glyph that beats initials for this kind of thing, where one exists. */
  readonly fallbackGlyph?: ReactElement;
  /**
   * Lets a composition adapt — an identity-led variant widening its name
   * column when no mark resolved — instead of every call site re-implementing
   * the same boolean.
   */
  readonly onState?: (state: CresultcardMarkAssetState) => void;
}

export function CresultcardIdentityMark({
  src,
  name,
  size = CresultcardMarkSize.Row,
  shape = CresultcardMarkShape.Square,
  monogram,
  fallbackSrc,
  fallbackGlyph,
  onState,
}: CresultcardIdentityMarkProps): ReactElement {
  const [state, setState] = useState<CresultcardMarkAssetState>(
    src == null
      ? CresultcardMarkAssetState.Absent
      : CresultcardMarkAssetState.Loading,
  );
  const nestedRadius = useCresultcardNestedRadius();
  const pixels = CRC_MARK_PIXELS[size];
  const isCircle = shape === CresultcardMarkShape.Circle;

  useEffect(() => {
    onState?.(state);
  }, [onState, state]);

  const handleLoad = useCallback(
    (event: SyntheticEvent<HTMLImageElement>): void => {
      setState(
        event.currentTarget.naturalWidth <= DEGENERATE_PIXEL_WIDTH
          ? CresultcardMarkAssetState.Failed
          : CresultcardMarkAssetState.Resolved,
      );
    },
    [],
  );

  const handleError = useCallback((): void => {
    setState(CresultcardMarkAssetState.Failed);
  }, []);

  const failed = state === CresultcardMarkAssetState.Failed;
  const absent = state === CresultcardMarkAssetState.Absent;
  const showFallback = failed || absent;

  return (
    <span
      aria-label={name}
      data-el="Cresultcard/IdentityMark/Root"
      data-crc-mark-state={state}
      role="img"
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        width: pixels,
        height: pixels,
        overflow: "hidden",
        boxSizing: "border-box",
        border: failed ? DASHED_HAIRLINE : SOLID_HAIRLINE,
        borderRadius: isCircle ? CIRCLE_RADIUS : nestedRadius,
        backgroundColor: CRC_TILE_BACKGROUND_COLOR,
      }}
      title={failed ? name : undefined}
    >
      {showFallback
        ? (fallbackGlyph ??
          (fallbackSrc == null ? (
            <span
              style={{
                fontSize: Math.round(pixels * MONOGRAM_SIZE_RATIO),
                fontWeight: 760,
                lineHeight: 1,
                color: CRC_TEXT_MUTED,
              }}
            >
              {monogram ?? cresultcardMonogram(name)}
            </span>
          ) : (
            /* Its own element, never the primary image with a `fallbackSrc`:
               the primary carries the degenerate-pixel guard, and a swap
               performed inside it would put the placeholder under that guard's
               measurement. It also carries NO `onLoad`/`onError` — the plate is
               the terminal state, so there is nothing left to fall back to.
               `Cover` rather than the primary's `Contain`: a supplied logo must
               not be cropped, and a placeholder designed for this plate must not
               be letterboxed inside a 22px mark. */
            <Cimage
              alt={name}
              animation={CIMAGE_ANIMATION.None}
              decoding={CRC_MEDIA_DECODING}
              height="100%"
              loading={CRC_MEDIA_LOADING}
              objectFit={CIMAGE_OBJECT_FIT.Cover}
              src={fallbackSrc}
              width="100%"
            />
          )))
        : null}
      {src != null && !failed && (
        <Cimage
          alt={name}
          animation={CIMAGE_ANIMATION.None}
          decoding={CRC_MEDIA_DECODING}
          height="100%"
          loading={CRC_MEDIA_LOADING}
          objectFit={CIMAGE_OBJECT_FIT.Contain}
          onError={handleError}
          onLoad={handleLoad}
          referrerPolicy={CRC_MEDIA_REFERRER_POLICY}
          src={src}
          width="100%"
        />
      )}
    </span>
  );
}
